// Memory product: three tabs under the Connect|Memory nav, one job per tab —
//   "Impact"         → convince: 3-stat dashboard + receipts feed (surfaced∩cited assists) from
//                      /me/assist-log, each receipt showing "pulled from N sessions". Default landing.
//   "Recent capture" → trust: a flat newest-first stream of what just got captured, from
//                      /me/recent-captures — a quick glance that capture is working (not the tree).
//   "Memory log"     → reference: the full context-tree, via the /me/tree renderer in an iframe.
// Seats are NOT here: buying one is money, and money lives on /account. This page owns where a
// session WRITES (the space bar), which is a memory question, not a billing one.
// A one-line "pulse" header spans all three tabs so the "it's working" signal shows without clicking
// in (shown only once there's a real assist, so a brand-new user never sees "0 of 0"). The tree src
// is a short-lived signed magic link.
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import {
  Box, Button, CircularProgress, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import { signInWithRedirect } from "../authConfig";
import { getMyTreeLink, setActiveWorkspace, getActingOrgId } from "../utils/api";
import MemoryNav from "../components/MemoryNav";
import MemoryReceipts from "../components/MemoryReceipts";
import RecentCaptures from "../components/RecentCaptures";
import TeamSpacesPanel from "../components/TeamSpacesPanel";
import useAssistLog from "../hooks/useAssistLog";
import useRecentCaptures from "../hooks/useRecentCaptures";
import useMyWorkspaces from "../hooks/useMyWorkspaces";
import { assistStats, formatTimeSaved } from "../utils/assistStats";
import { accountRedirectFor } from "../utils/accountRedirect";

const TEAL = "#2da894";

// The single line of truth for the view/route split. VIEWING (what this page shows,
// URL ?space=, ephemeral) is decoupled from ROUTING (where the user's sessions read and
// save memory, active_workspace, durable) — browsing a space must never re-route the
// account. Shown whenever either differs from the personal default:
//   viewing a space, routing here      → "Viewing X · sessions read/save here · Stop contributing"
//   viewing a space, routing elsewhere → "Viewing X · sessions still write to P · Contribute here"
//   viewing Personal, routing to Y     → "Sessions read/save to Y · View · Stop contributing"
const PINNING_FLOOR = [0, 2, 4];   // plugin version that introduced session pinning
function versionAtLeast(v, floor) {
  const parts = String(v || "").split(".").map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < floor.length; i++) {
    const a = parts[i] || 0;
    if (a > floor[i]) return true;
    if (a < floor[i]) return false;
  }
  return true;
}
function pinsSessions(plugin) {
  // Dormant clients don't veto: the conservative copy exists to protect RUNNING old-plugin
  // sessions (they follow a default flip live), and no session survives a week — so a client
  // not seen in 7 days can't be running one. (Founder case: a Codex install last seen three
  // weeks ago held the copy conservative despite every live client pinning.) Unknown
  // timestamps are kept — conservative by default.
  const DORMANT_MS = 7 * 24 * 3600 * 1000;
  const now = Date.now();
  const clients = Object.values(plugin?.clients || {}).filter((c) => {
    const t = c?.at ? Date.parse(c.at) : NaN;
    return !Number.isFinite(t) || now - t < DORMANT_MS;
  });
  return clients.length > 0 && clients.every((c) => versionAtLeast(c?.version, PINNING_FLOOR));
}

function SpaceBar({ viewedSpace, activeSpace, busy, plugin, onContribute, onStop, onViewActive }) {
  if (!viewedSpace && !activeSpace) return null;
  const name = (s) => s?.name || s?.workspace;
  const routedHere = Boolean(viewedSpace && activeSpace?.workspace === viewedSpace.workspace);
  // Per-viewer routing copy: session pinning is a CAPABILITY FLOOR (shipped v0.2.4),
  // not equality-with-latest — a 0.3.0 client while latest is 0.3.2 is "outdated" but
  // absolutely pins. Conservative when any known client is below the floor (its
  // sessions still follow the default live) or when no client has reported yet.
  const pinned = pinsSessions(plugin);
  const others = viewedSpace ? (viewedSpace.member_count || 1) - 1 : 0;
  const linkSx = {
    appearance: "none", background: "none", border: 0, cursor: "pointer", p: 0,
    color: "#9aa0aa", fontSize: 12, textDecoration: "underline", fontFamily: "inherit",
    whiteSpace: "nowrap", "&:hover": { color: "#fff" }, "&:disabled": { opacity: 0.5 },
  };
  const teal = { color: TEAL, fontWeight: 700 };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: { xs: 2, md: 4 }, py: 0.75,
               bgcolor: "rgba(45,168,148,.10)", borderBottom: "1px solid rgba(45,168,148,.28)" }}>
      <FolderSharedOutlinedIcon sx={{ fontSize: 16, color: TEAL }} />
      <Typography sx={{ color: "#bfe3dc", fontSize: 12.5 }}>
        {viewedSpace ? (
          <>
            Viewing <Box component="span" sx={teal}>{name(viewedSpace)}</Box>
            {others > 0 && <> · shared with {others} teammate{others !== 1 ? "s" : ""}</>}
            {" · "}
            {routedHere
              ? (pinned ? <>default for new sessions</> : <>your sessions read from and save memory here</>)
              : <>{pinned ? "new sessions start in" : "your sessions still write to"}{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                   {activeSpace ? name(activeSpace) : "Personal"}</Box>
                  {!activeSpace && <> (the Assertion app keeps each project in its own space)</>}</>}
          </>
        ) : (
          <>
            {pinned ? "New sessions start in" : "Your sessions read from and save memory to"}{" "}
            <Box component="span" sx={teal}>{name(activeSpace)}</Box>
          </>
        )}
      </Typography>
      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
        {!viewedSpace && activeSpace && (
          <Box component="button" onClick={onViewActive} disabled={busy} sx={linkSx}>
            View {name(activeSpace)}
          </Box>
        )}
        {viewedSpace && !routedHere && (
          <Box component="button" onClick={onContribute} disabled={busy}
               sx={{ ...linkSx, color: TEAL, fontWeight: 700, "&:hover": { color: "#3fd4bb" } }}>
            {pinned ? "Make default" : "Contribute here"}
          </Box>
        )}
        {(routedHere || (!viewedSpace && activeSpace)) && (
          <Box component="button" onClick={onStop} disabled={busy} sx={linkSx}>
            {pinned ? "Reset default" : "Stop contributing"}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function subTab(active) {
  return {
    appearance: "none", background: "none", border: 0, cursor: "pointer",
    padding: "0 2px", height: "100%", display: "flex", alignItems: "center",
    color: active ? "#fff" : "#9aa0aa", fontWeight: 600, fontSize: 13.5,
    borderBottom: active ? `2px solid ${TEAL}` : "2px solid transparent",
    fontFamily: "inherit",
  };
}

// Spans the utility tabs: the always-visible "it's alive / learning" signal. Hidden until there's at
// least one real assist, so a brand-new tree never renders "0 of 0".
function PulseHeader({ data }) {
  const totals = data?.totals || {};
  const surfaced = totals.turns || 0;
  const { helpedTurns: assisted, xsessionTurns: xsession, hoursSaved } =
    assistStats(data?.events, data?.nodes, data?.counter);
  if (assisted < 1) return null;
  // Denominator = all-active turns (the per-turn auto-recall search counter). Guarded: fall back to
  // the surfaced-turn window count if it's missing or (defensively) below the numerator, so the ratio
  // can never render > 100%. "recent" is dropped once the counter makes the numbers lifetime.
  const lifetime = data?.counter != null;
  const active = data?.active_turns;
  const denom = active != null && active >= assisted ? active : surfaced;
  const saved = formatTimeSaved(hoursSaved);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: { xs: 2, md: 4 },
               py: 0.75, bgcolor: "#0e0e14", borderBottom: "1px solid #1d1d25" }}>
      <PsychologyOutlinedIcon sx={{ fontSize: 16, color: TEAL }} />
      <Typography sx={{ color: "#b9bec7", fontSize: 12.5 }}>
        Memory helped on{" "}
        <Box component="span" sx={{ color: TEAL, fontWeight: 700 }}>{assisted}</Box> of {denom}{lifetime ? "" : " recent"} turns
        {xsession > 0 && (
          <> · <Box component="span" sx={{ color: TEAL, fontWeight: 700 }}>{xsession}</Box> across sessions</>
        )}
        {saved.show && (
          <> · <Box component="span"
                 title="Estimated re-explaining time saved: cross-session facts memory recalled that you'd otherwise restate, charged only for the fact text at a conservative composition-typing rate (Karat 1999, 19 wpm). A floor — excludes lookup, redo, and the effort of tracking it yourself."
                 sx={{ color: TEAL, fontWeight: 700 }}>{saved.value} {saved.unit} saved</Box></>
        )}
      </Typography>
    </Box>
  );
}

export default function Memory() {
  const { instance, inProgress } = useMsal();
  const isAuth = useIsAuthenticated();
  // Active tab is derived from the URL (?tab=recent|log) so a refresh / shared link restores it.
  // "impact" is the default landing and keeps the URL clean (no param).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const tab = rawTab === "recent" ? "recent" : rawTab === "log" ? "log" : "impact";
  // Node to land on in the Memory-log tree (set when a Recent-capture card is opened). Cleared on a
  // plain tab switch so the tree doesn't re-focus a stale node the next time you open the log.
  const [focusNode, setFocusNode] = useState(null);
  // PUSH a history entry per tab switch (not replace) so browser Back/Forward walk the tabs you
  // visited instead of jumping straight out of /memory. Refresh/shared-link still work (?tab in URL).
  const selectTab = (t) => {
    setFocusNode(null);
    const p = t === "impact" ? {} : { tab: t };
    const sp = searchParams.get("space");
    if (sp) p.space = sp;                     // tab switches keep the viewed space
    setSearchParams(p);
  };
  // From a Recent-capture card: jump into the Memory log landed (oriented) on that node.
  // PUSH (not replace) a history entry so browser Back returns to Recent capture, not out of /memory.
  const openInTree = (id) => {
    setFocusNode(id);
    const p = { tab: "log" };
    const sp = searchParams.get("space");
    if (sp) p.space = sp;
    setSearchParams(p);
  };
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsAuth, setNeedsAuth] = useState(false);
  const expiresAtRef = useRef(0);   // unix secs the current tree-link token expires (from /me/tree-link)

  // Impersonation is read-only inspection of SOMEONE ELSE's scope — the admin's OWN space
  // switcher / space bar rendered beside "Viewing: org/user" reads as if the two compose
  // (they don't). Hidden entirely while acting; clear impersonation to manage your own spaces.
  const impersonating = Boolean(getActingOrgId());
  // VIEW state lives in the URL (?space=): ephemeral, shareable, per-tab — and completely
  // decoupled from routing. Data fetches trust the raw param (the backend validates
  // membership and falls back silently); the bar/switcher only CLAIM "Viewing X" once
  // membership is confirmed from /me/workspaces.
  const rawSpace = impersonating ? null : (searchParams.get("space") || null);

  const assist = useAssistLog(rawSpace);
  const recent = useRecentCaptures(rawSpace);
  // Team spaces: feeds the nav's SpaceSwitcher, the SpaceBar, and the manage panel.
  const workspaces = useMyWorkspaces();
  const [teamPanelOpen, setTeamPanelOpen] = useState(false);
  const spaces = workspaces.data?.spaces || [];
  const viewedSpace = impersonating ? null
    : (spaces.find((s) => s.workspace === rawSpace) || null);
  const activeSpace = impersonating ? null
    : (spaces.find((s) => s.workspace === workspaces.data?.active) || null);

  // ROUTING actions (the only writes): "Contribute here" asks for one explicit
  // confirmation — it redirects every session, including ones already running.
  // "Stop contributing" is the safe direction: immediate.
  const [confirmContribute, setConfirmContribute] = useState(false);
  const [routeBusy, setRouteBusy] = useState(false);
  const contributeHere = async () => {
    if (!viewedSpace) return;
    setRouteBusy(true);
    try { await setActiveWorkspace(viewedSpace.workspace); await workspaces.refresh(); setConfirmContribute(false); }
    catch (_) {} finally { setRouteBusy(false); }
  };
  const stopContributing = async () => {
    setRouteBusy(true);
    try { await setActiveWorkspace(null); await workspaces.refresh(); }
    catch (_) {} finally { setRouteBusy(false); }
  };

  // VIEW navigation (no writes): switcher picks what this page shows. Keeps the current tab.
  const selectViewSpace = (ws) => {
    setFocusNode(null);
    const p = {};
    if (tab !== "impact") p.tab = tab;
    if (ws) p.space = ws;
    setSearchParams(p);
  };

  // Give each tab a distinct document.title so browser history / back-forward entries are
  // labeled ("Impact · Memory" etc.) instead of all reading the static "Assertion AI Studio".
  useEffect(() => {
    const names = { impact: "Impact", recent: "Recent capture", log: "Memory log" };
    document.title = `${names[tab] || "Memory"} · Memory · Assertion AI Studio`;
    return () => { document.title = "Assertion AI Studio"; };
  }, [tab]);

  // Viewed space changes invalidate the minted tree link (it's scope-bearing).
  useEffect(() => { setUrl(null); }, [rawSpace]);

  const fetchLink = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { url, expires_at } = await getMyTreeLink(rawSpace);
      setUrl(url);
      expiresAtRef.current = expires_at || 0;
      setNeedsAuth(false);
    } catch (e) {
      // Silent token unavailable -> offer the page's own Sign in (returns here) instead of a
      // full-page redirect loop. Any other error surfaces as a message.
      if (e?.message === "INTERACTION_REQUIRED") setNeedsAuth(true);
      else if (e?.message !== "REDIRECTING_FOR_AUTH") {
        setError(e?.response?.data?.error || e?.message || "Could not load your memory tree.");
      }
    } finally {
      setLoading(false);
    }
  }, [rawSpace]);

  // Only mint a tree link when the user actually opens the Memory-log tab (the iframe) — no need to
  // mint (and then keep refreshing) a token for someone who lands on Impact and never opens the tree.
  useEffect(() => {
    if (isAuth && inProgress === InteractionStatus.None && tab === "log" && !url) fetchLink();
  }, [isAuth, inProgress, tab, url, fetchLink]);

  // Keep the tree-link token fresh so the iframe never re-navigates with an expired ?t= (which
  // would make /me/tree 401 → the browser's Basic-Auth popup). Re-mint only when the token is
  // within ~5 min of its 60-min expiry, checked on tab-focus and a periodic timer — so an idle
  // page/tab-return refreshes the token instead of triggering the popup, without reloading the
  // iframe on every focus.
  useEffect(() => {
    if (!isAuth) return;
    const maybeRefresh = () => {
      if (document.visibilityState === "hidden" || !expiresAtRef.current) return;
      const now = Math.floor(Date.now() / 1000);
      if (now >= expiresAtRef.current - 300) fetchLink();
    };
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);
    const id = setInterval(maybeRefresh, 5 * 60 * 1000);   // check every 5 min while open
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
      clearInterval(id);
    };
  }, [isAuth, fetchLink]);

  const signIn = async () => {
    setError("");
    try {
      await signInWithRedirect(instance);   // full page, back here after (see authConfig.js)
    } catch (e) {
      setError(e?.message || "Sign-in failed. Please try again.");
    }
  };

  const showAuth = !isAuth || needsAuth;
  // In local dev the data-backed tabs (Impact/Recent) render without sign-in — the assist feed falls
  // back to a prod snapshot so they're reviewable even when localhost B2C isn't set up. Never in prod.
  const devPreview = process.env.NODE_ENV !== "production";
  const ready = !showAuth || devPreview;
  const impactNow = tab === "impact" && ready;
  const recentNow = tab === "recent" && ready;
  // Pulse header spans all three tabs — the persistent "it's alive / learning" signal.
  const showPulse = ready;

  // The plan lives on /agent/account now. Installed agents (ASSERTION_UPGRADE_URL) and in-flight
  // Stripe returns still arrive here with ?upgrade=1 / ?checkout= — hand them on, after every hook.
  const handoff = accountRedirectFor(searchParams.toString());
  if (handoff) return <Navigate to={handoff} replace />;

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "#0b0b0f" }}>
      <MemoryNav spaceState={impersonating ? null : workspaces.data}
                 viewedWorkspace={viewedSpace?.workspace || null}
                 onSelectSpace={selectViewSpace}
                 onOpenTeamPanel={() => setTeamPanelOpen(true)} />
      <TeamSpacesPanel
        open={teamPanelOpen}
        onClose={() => setTeamPanelOpen(false)}
        workspaces={workspaces.data}
        meUid={workspaces.data?.me}
        onRefresh={workspaces.refresh}
      />

      {/* Routing is the one consequential act — confirm it explicitly (view/browse never asks). */}
      <Dialog open={confirmContribute} onClose={() => setConfirmContribute(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 17 }}>
          Contribute to {viewedSpace?.name || viewedSpace?.workspace}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {pinsSessions(assist.data?.plugin) ? (
              <>New sessions will <b>read from and save memory to this space</b>. Sessions already
              running keep their current space — switch one in-session by telling your assistant
              "switch my memory to {viewedSpace?.name || viewedSpace?.workspace}".</>
            ) : (
              <>Your sessions — including any already running — will <b>read from and save memory to
              this space</b> until you stop contributing. Your personal memory won't be used or
              updated while it's active.</>
            )}
            {(viewedSpace?.member_count || 1) > 1 && (
              <> Everything captured here is visible to its {viewedSpace.member_count} members.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmContribute(false)} disabled={routeBusy}
                  sx={{ textTransform: "none", color: "text.secondary" }}>
            Cancel
          </Button>
          <Button variant="contained" disabled={routeBusy} onClick={contributeHere}
                  sx={{ textTransform: "none", boxShadow: "none", bgcolor: TEAL,
                        "&:hover": { bgcolor: "#1f8a78" } }}>
            {routeBusy ? "Switching…" : (pinsSessions(assist.data?.plugin) ? "Make default" : "Contribute here")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* sub-tabs */}
      <Box sx={{ display: "flex", alignItems: "stretch", height: 44, gap: 3,
                 px: { xs: 2, md: 4 }, bgcolor: "#0b0b0f", borderBottom: "1px solid #1d1d25" }}>
        <button style={subTab(tab === "impact")} onClick={() => selectTab("impact")}>Impact</button>
        <button style={subTab(tab === "recent")} onClick={() => selectTab("recent")}>Recent capture</button>
        <button style={subTab(tab === "log")} onClick={() => selectTab("log")}>Memory log</button>
      </Box>

      {!impersonating && (
        <SpaceBar viewedSpace={viewedSpace} activeSpace={activeSpace} busy={routeBusy}
                  plugin={assist.data?.plugin}
                  onContribute={() => setConfirmContribute(true)}
                  onStop={stopContributing}
                  onViewActive={() => selectViewSpace(activeSpace?.workspace)} />
      )}
      {showPulse && <PulseHeader data={assist.data} />}

      <Box sx={{ flex: 1, position: "relative", minHeight: 0 }}>
        {impactNow ? (
          <MemoryReceipts data={assist.data} loading={assist.loading} usedFixture={assist.usedFixture} />
        ) : recentNow ? (
          <RecentCaptures items={recent.items} loading={recent.loading} onOpen={openInTree} />
        ) : showAuth ? (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column",
                     alignItems: "center", justifyContent: "center", gap: 2, color: "#fff" }}>
            <Typography sx={{ opacity: 0.7 }}>Sign in to see your memory.</Typography>
            <Button variant="contained" onClick={signIn}>Sign in</Button>
          </Box>
        ) : loading || !url ? (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="iframe"
               key={focusNode || "tree"}
               src={focusNode ? `${url}#focus=${encodeURIComponent(focusNode)}` : url}
               title="Your memory tree"
               sx={{ border: 0, width: "100%", height: "100%", display: "block", bgcolor: "#fff" }} />
        )}
        {error && (
          <Typography color="error" sx={{ position: "absolute", bottom: 16, left: 0, right: 0, textAlign: "center" }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
